import {
  ContentTypeEnum,
  LangEnum,
  SourcePagesEnum,
  SourceWidgetsEnum,
} from "@/constants/constants";
import { ContentInterface } from "@/types/home.types";
import {
  MappedSearchResult,
  SearchResult,
  TrendingSearchRes,
} from "@/types/search.types";
import {
  captureError,
  generateContentDetailURL,
  generateURLSlug,
  getContentTypeUrlValue,
  getThumbnailsFromObject,
} from "@/utils/common.helpers";
import { API_ROUTES } from "../apiRoutes";
import { REQUEST_METHODS } from "../configs.apiClient";
import { HttpClient } from "../HttpClient";

export class SearchAPIClient {
  private httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  // return list of all content in english
  async getSearchResult({
    dialect,
    lang,
    searchQuery,
  }: {
    dialect: string;
    lang: string;
    searchQuery: string;
  }): Promise<MappedSearchResult> {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.search.searchResult;

      const response = await this.httpClient.request<{
        responseMessage: string;
        data: SearchResult;
      }>({
        endpoint,
        options: {
          method,
        },
        addToken: true,
        dialect,
        lang: lang,
        queryParams: {
          keyword: searchQuery,
        },
      });

      let enRes = response;
      if (lang !== LangEnum.EN) {
        enRes = await this.httpClient.request<{
          responseMessage: string;
          data: SearchResult;
        }>({
          endpoint,
          options: {
            method,
          },
          addToken: true,
          dialect,
          lang: LangEnum.EN,
          queryParams: {
            keyword: searchQuery,
          },
        });
      }

      const searchDataList: ContentInterface[] =
        response.data.searchDataList.map((item) => {
          const enTitle =
            enRes.data.searchDataList.find(
              (enItem) => enItem.slug === item.slug,
            )?.title || "";

          const slug = generateURLSlug({
            contentName: enTitle,
            fallbackSlug: item.slug,
            contentId: item._id,
          });
          const { _id, title, type } = item;

          const thumbnails = getThumbnailsFromObject(
            item.thumbnail,
            item.type === ContentTypeEnum.SHOW,
            "small",
            "ratio1",
          );

          const contentTypeUrlValue = getContentTypeUrlValue(
            item.type,
            item.format,
          );
          const contentUrl = generateContentDetailURL(
            lang as LangEnum,
            dialect,
            contentTypeUrlValue,
            slug,
          );
          const displayTags = item.displayTags || [];
          return {
            _id,
            title,
            type,
            contentUrl,
            slug,
            description: item.description,
            ...thumbnails,
            displayTags,
          };
        });

      return {
        searchDataList,
        referralWidgetId: response.data.referralWidgetId,
        keyword: response.data.keyword,
        isPopular: response.data.isPopular,
      } as MappedSearchResult;
    } catch (error) {
      console.error("Error in SearchAPIClient getSearchResult", error);
      captureError(error);
      return {
        searchDataList: [],
        referralWidgetId: 0,
        keyword: searchQuery,
        isPopular: false,
      };
    }
  }

  async getTrendingSearchRecords({
    dialect,
    lang,
  }: {
    dialect: string;
    lang: string;
  }): Promise<ContentInterface[]> {
    try {
      const method = REQUEST_METHODS.GET;
      const endpoint = API_ROUTES.search.trendingSearchRecords;

      const response = await this.httpClient.request<{
        data: TrendingSearchRes;
      }>({
        endpoint,
        options: {
          method,
        },
        addToken: true,
        dialect,
        lang: lang,
      });

      let enRes = response;
      if (lang !== LangEnum.EN) {
        enRes = await this.httpClient.request<{
          data: TrendingSearchRes;
        }>({
          endpoint,
          options: {
            method,
          },
          addToken: false,
          dialect,
          lang: LangEnum.EN,
        });
      }

      const trendingSearchData = response.data.result.map((item) => {
        const { id, title } = item;

        const enTitle =
          enRes.data.result.find((enItem) => enItem.slug === item.slug)
            ?.title || "";

        const slug = generateURLSlug({
          contentName: enTitle,
          fallbackSlug: item.slug,
          contentId: item.id,
        });

        const contentTypeUrlValue = getContentTypeUrlValue(
          item.type,
          item.format,
        );

        const contentUrl = generateContentDetailURL(
          lang as LangEnum,
          dialect,
          contentTypeUrlValue,
          slug,
        );

        const thumbnails = getThumbnailsFromObject(
          item.thumbnail,
          item.type === ContentTypeEnum.SHOW,
          "small",
          "ratio1",
        );
        return {
          _id: id,
          title: item.title,
          contentUrl,
          type: item.type,
          slug: item.slug,
          description: item.description,
          ...thumbnails,
        };
      });

      return trendingSearchData;
    } catch (error) {
      console.error("Error in SearchAPIClient getTrendingSearchRecords", error);
      captureError(error);
      return [];
    }
  }
}
